import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Paper,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Button,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { auditLogService } from '../services/auditLogService';
import { AuditLog } from '../types/api';
import { format } from 'date-fns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export const AuditLogPage: React.FC = () => {
  const [paginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    entityType: '',
    startDate: '',
    endDate: '',
  });

  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', paginationModel.page + 1, paginationModel.pageSize, filters],
    queryFn: () =>
      auditLogService.getAll({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        entityType: filters.entityType || undefined,
        from: filters.startDate || undefined,
        to: filters.endDate || undefined,
      }),
    placeholderData: (previousData) => previousData,
  });

  const handleRowClick = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getActionColor = (action: string): 'success' | 'warning' | 'error' | 'info' => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'created':
        return 'success';
      case 'update':
      case 'updated':
        return 'info';
      case 'delete':
      case 'deleted':
        return 'error';
      default:
        return 'warning';
    }
  };


  const renderExpandedRow = (log: AuditLog) => {
    if (!log.oldValue && !log.newValue) return null;

    return (
      <Collapse in={expandedRow === log.id}>
        <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {log.oldValue && (
              <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Old Value
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(log.oldValue, null, 2)}
                </Typography>
              </Paper>
            )}
            {log.newValue && (
              <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  New Value
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(log.newValue, null, 2)}
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      </Collapse>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Audit Logs
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <TextField
            label="User ID"
            size="small"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Action"
            size="small"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            placeholder="create, update, delete"
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Entity Type"
            size="small"
            value={filters.entityType}
            onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
            placeholder="BlacklistEntry"
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Start Date"
            type="datetime-local"
            size="small"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="End Date"
            type="datetime-local"
            size="small"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="outlined"
            onClick={() =>
              setFilters({
                userId: '',
                action: '',
                entityType: '',
                startDate: '',
                endDate: '',
              })
            }
          >
            Clear
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ height: 600, width: '100%' }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Entity Type</TableCell>
                <TableCell>Entity ID</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : !data || data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {data.data.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow hover>
                        <TableCell>{format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}</TableCell>
                        <TableCell>{log.userId || 'System'}</TableCell>
                        <TableCell>
                          <Chip label={log.action} size="small" color={getActionColor(log.action)} />
                        </TableCell>
                        <TableCell>{log.entityType}</TableCell>
                        <TableCell>{log.entityId}</TableCell>
                        <TableCell>
                          {(log.oldValue || log.newValue) && (
                            <IconButton size="small" onClick={() => handleRowClick(log.id)}>
                              {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      {renderExpandedRow(log)}
                    </React.Fragment>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
